import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import {
  LuPlus, LuPencil, LuTrash2, LuCheck, LuBuilding,
} from 'react-icons/lu';
import { Modal, errorMessage, fmtDate } from './trainingUtils';

// Listas completas, sem o filtro de "empresa ativa" do CompanySwitcher —
// a liberação é decidida pela DHR para qualquer empresa cliente.
const ALL_COMPANIES = { headers: { 'X-Active-Company-Id': '' } };

function ReleaseModal({ courseId, editing, companies, departmentsByCompany, releasedCompanyIds, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    company_id: editing?.company_id || '',
    department_ids: editing?.department_ids || [],
    allDepartments: !editing || !(editing.department_ids || []).length,
    due_date: editing?.due_date || '',
    mandatory: !!editing?.mandatory,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const departments = departmentsByCompany[form.company_id] || [];

  const toggleDept = (id) => setForm((f) => ({
    ...f,
    department_ids: f.department_ids.includes(id) ? f.department_ids.filter((d) => d !== id) : [...f.department_ids, id],
  }));

  const handleSave = async () => {
    if (!form.company_id) { setError('Selecione a empresa.'); return; }
    if (!form.allDepartments && !form.department_ids.length) { setError('Selecione ao menos um departamento ou libere para todos.'); return; }
    setSaving(true); setError('');
    const payload = {
      company_id: Number(form.company_id),
      department_ids: form.allDepartments ? [] : form.department_ids,
      due_date: form.due_date || null,
      mandatory: form.mandatory,
    };
    try {
      if (editing) await api.put(`/training/releases/${editing.id}`, payload);
      else await api.post(`/training/courses/${courseId}/releases`, payload);
      onSaved();
    } catch (e) {
      setError(errorMessage(e)); setSaving(false);
    }
  };

  return (
    <Modal title={editing ? 'Editar liberação' : 'Liberar para empresa'} icon={<LuBuilding color="var(--accent)" />} onClose={onClose} maxWidth={560}>
      {error && <div className="alert-custom danger">{error}</div>}
      <div>
        <label className="form-label">Empresa *</label>
        <select className="form-control" value={form.company_id} disabled={!!editing}
          onChange={(e) => setForm((f) => ({ ...f, company_id: e.target.value, department_ids: [] }))}>
          <option value="">Selecione…</option>
          {companies
            .filter((c) => editing || !releasedCompanyIds.includes(c.id))
            .map((c) => <option key={c.id} value={c.id}>{c.trade_name || c.name}{c.type === 'filial' ? ' (filial)' : ''}</option>)}
        </select>
      </div>
      {form.company_id && (
        <div>
          <label className="form-label">Departamentos</label>
          <label className="training-check" style={{ marginBottom: 6 }}>
            <input type="checkbox" checked={form.allDepartments} onChange={(e) => set('allDepartments', e.target.checked)} />
            Todos os departamentos da empresa
          </label>
          {!form.allDepartments && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8 }}>
              {!departments.length && <span className="training-hint">Esta empresa não tem departamentos cadastrados.</span>}
              {departments.map((d) => (
                <label key={d.id} className="training-check">
                  <input type="checkbox" checked={form.department_ids.includes(d.id)} onChange={() => toggleDept(d.id)} /> {d.name}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="training-form-grid">
        <div>
          <label className="form-label">Prazo de conclusão</label>
          <input className="form-control" type="date" value={form.due_date || ''} onChange={(e) => set('due_date', e.target.value)} />
        </div>
      </div>
      <label className="training-check">
        <input type="checkbox" checked={form.mandatory} onChange={(e) => set('mandatory', e.target.checked)} />
        Curso obrigatório para esta empresa
      </label>
      <div className="training-modal-footer">
        <button type="button" className="training-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="training-btn training-btn--primary" onClick={handleSave} disabled={saving}><LuCheck /> {saving ? 'Salvando…' : 'Salvar liberação'}</button>
      </div>
    </Modal>
  );
}

export default function TrainingReleaseTab({ course }) {
  const [releases, setReleases] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [enabledIds, setEnabledIds] = useState(null);
  const [modal, setModal] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/training/courses/${course.id}/releases`);
      setReleases(data);
    } catch (e) {
      setError(errorMessage(e, 'Erro ao carregar as liberações'));
    }
  }, [course.id]);

  useEffect(() => {
    load();
    api.get('/companies', ALL_COMPANIES).then((r) => setCompanies(Array.isArray(r.data) ? r.data : r.data.rows || [])).catch(() => {});
    api.get('/departments', ALL_COMPANIES).then((r) => setDepartments(r.data)).catch(() => {});
    api.get('/company-modules')
      .then((r) => setEnabledIds(r.data.companies.filter((c) => c.modules.training?.enabled).map((c) => c.id)))
      .catch(() => setEnabledIds([]));
  }, [load]);

  const departmentsByCompany = useMemo(() => departments.reduce((acc, d) => {
    (acc[d.company_id] = acc[d.company_id] || []).push(d);
    return acc;
  }, {}), [departments]);

  const handleDelete = async (r) => {
    const name = r.company?.trade_name || r.company?.name;
    if (!window.confirm(`Remover a liberação para "${name}"? Novos alunos dessa empresa deixam de ver o curso; o histórico de quem já começou é mantido.`)) return;
    try {
      await api.delete(`/training/releases/${r.id}`);
      load();
    } catch (e) {
      setError(errorMessage(e, 'Erro ao remover'));
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      {error && <div className="alert-custom danger">{error}</div>}
      {course.status !== 'published' && (
        <div className="alert-custom warning">O curso está como {course.status === 'draft' ? 'rascunho' : 'arquivado'}: as liberações só ficam visíveis aos colaboradores quando ele estiver publicado.</div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <div className="training-hint" style={{ marginTop: 0 }}>
          O colaborador só vê o curso se ele estiver liberado para a empresa e o departamento dele. Só aparecem empresas com o módulo
          Treinamentos habilitado em <strong>Administrador Global → Módulos por Empresa</strong>.
        </div>
        <button type="button" className="training-btn training-btn--primary" onClick={() => setModal('new')}><LuPlus /> Liberar para empresa</button>
      </div>

      {!releases.length ? (
        <div className="empty-state"><LuBuilding size={32} color="var(--text-3)" /><p>Curso ainda não liberado para nenhuma empresa</p></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="training-table">
            <thead>
              <tr><th>Empresa</th><th>Departamentos</th><th>Prazo</th><th>Obrigatório</th><th /></tr>
            </thead>
            <tbody>
              {releases.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 600, color: 'var(--text-1)' }}>
                    {r.company?.trade_name || r.company?.name}
                    {!r.module_enabled && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)' }}>Módulo desabilitado para esta empresa</div>}
                  </td>
                  <td>{r.departments.length ? r.departments.map((d) => d.name).join(', ') : 'Todos'}</td>
                  <td style={{ color: r.due_date && r.due_date < today ? 'var(--danger)' : undefined }}>{fmtDate(r.due_date)}</td>
                  <td>{r.mandatory ? 'Sim' : 'Não'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" className="training-icon-btn" title="Editar" onClick={() => setModal(r)}><LuPencil /></button>
                    <button type="button" className="training-icon-btn training-icon-btn--danger" title="Remover liberação" onClick={() => handleDelete(r)}><LuTrash2 /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <ReleaseModal
          courseId={course.id}
          editing={modal === 'new' ? null : modal}
          companies={modal === 'new' && enabledIds ? companies.filter((c) => enabledIds.includes(c.id)) : companies}
          departmentsByCompany={departmentsByCompany}
          releasedCompanyIds={releases.map((r) => r.company_id)}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
